// How the clinician controls an assessment, chosen on the Assessment Selection screen. Voice commands
// work in both modes.
//   remote   - BLE remote buttons; hand-raise detection is off
//   gestures - hand raise; remote buttons are ignored

export type AssessmentControl = "remote" | "gestures";

export const DEFAULT_ASSESSMENT_CONTROL: AssessmentControl = "remote";

const STORAGE_KEY = "assessmentControl";

export function getAssessmentControl(): AssessmentControl {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "remote" || value === "gestures" ? value : DEFAULT_ASSESSMENT_CONTROL;
  } catch {
    return DEFAULT_ASSESSMENT_CONTROL;
  }
}

export function setAssessmentControl(control: AssessmentControl) {
  localStorage.setItem(STORAGE_KEY, control);
}

export function isRemoteControl(): boolean {
  return getAssessmentControl() === "remote";
}

// Remote versions of the audio prompts that tell the therapist to raise a hand (scripts in
// docs/remote-audio-prompts.md)
const REMOTE_AUDIO: Record<string, string> = {
  "audios/gait/Gait_Start_Assessment_we_are_ready_to_begin.mp3":
    "audios/gait/remote/Gait_Start_Assessment_remote.mp3",
  "audios/tug/TUG_StartAssessment_we_are_ready_to_begin.mp3":
    "audios/tug/remote/TUG_StartAssessment_remote.mp3",
  "audios/sitstand/standing_height_gesture_we_are_ready.mp3":
    "audios/sitstand/remote/standing_height_remote.mp3",
  "audios/functionalReach/Ready_Therapist_should_raise_one_arm_or_say_we_are_ready_to_begin.mp3":
    "audios/functionalReach/remote/FR_Ready_remote.mp3",
  "audios/upper_body_arom/AROM_Shoulder_Abduction_and_Adduction_we_are_ready_to_begin.mp3":
    "audios/upper_body_arom/remote/AROM_AddAbd_remote.mp3",
  "audios/mCTSIB/mCTSIB_Start_Assessment_we_are_ready_to_begin.mp3":
    "audios/mCTSIB/remote/mCTSIB_Start_Assessment_remote.mp3",
};

// The clip to play for `path` in the current control mode
export function controlAudio(path: string): string {
  return isRemoteControl() && REMOTE_AUDIO[path] ? REMOTE_AUDIO[path] : path;
}

// `path` in remote control mode, null with hand gestures: for a prompt only the remote has (e.g. each
// later AROM movement's "press Start", where the hand-gesture flow plays nothing)
export function remoteOnlyAudio(path: string): string | null {
  return isRemoteControl() ? path : null;
}
